import * as React from "react";
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Stack,
  Heading,
  Text,
  Image,
  Icon as ChakraIcon,
  Button,
  Container,
  useToast,
  Link,
  Grid,
  GridItem,
  Skeleton} from "@chakra-ui/react";
import { PageSlideFade } from "../components/page-transitions";
import { apiHost } from "utils/Cons/Constants";
import axios from "axios";
import { Helmet } from "react-helmet";
import parser from "html-react-parser";
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L, { Icon } from 'leaflet';
import markerIconPng from "../assets/images/marker-icon.png";
import ShareModal from "components/share-modal";
import { FaHeart } from "react-icons/fa";

interface Event {
  id: number;
  title: string;
  slug: string;
  content?: string;
  meta_description?: string;
  header_img?: string;
  main_img?: string;
  summary?: string;
  starts_display?: string;
  starts?: string;
  ends?: string;
  ends_display?: string;
  google_link?: string;
  website?: string;
  favorited?: boolean;
  recurrence?: string;
  r_yearly_first?: boolean;
  r_yearly_second?: boolean;
  r_yearly_month?: string;
  r_yearly_day?: number;
  r_yearly_weekday?: string;
  r_yearly_second_month?: string;
  r_monthly_first?: boolean;
  r_monthly_second?: boolean;
  r_monthly_months?: number;
  r_monthly_day?: number;
  r_yearly_occurrence?: string;
  r_monthly_occurrence?: string;
  r_monthly_weekday?: string;
  r_weekly_weeks?: number;
  r_weekly_day?: string[];
  r_daily_days?: number;
  r_end_date?: string;
  r_end_type?: string;
  r_number_of_occurrences?: number;
  display_dates?: boolean | number;
  location_formatted_latitude?: number;
  location_formatted_longitude?: number;
}

interface Variables {
  default_event_img?: string;
}

const Event = ({ match }) => {
  const toast = useToast();

  const [event, setEvent] = useState<Event>({} as Event);
  const [loading, setLoading] = useState(true);
  const [variables, setVariables] = useState<Variables>({});

  const position = [40.9133945, -77.7782867];

  const history = useHistory();

  // ChangeView component for updating map view
  const ChangeView = ({ center, zoom }) => {
    const map = useMap();
    map.setView(center, zoom);
    return null;
  };

  // Function to parse PHP serialized array
  const parsePHPSerializedArray = (serializedString: any): string[] => {
    if (!serializedString || typeof serializedString !== 'string') {
      return [];
    }
    
    // Check if it's a PHP serialized array
    if (serializedString.startsWith('a:')) {
      try {
        // Extract the array content using regex
        const match = serializedString.match(/a:\d+:{(.*)}/);
        if (match) {
          const content = match[1];
          const values = [];
          
          // Parse the serialized content to extract string values
          const stringMatches = content.match(/s:\d+:"([^"]+)"/g);
          if (stringMatches) {
            stringMatches.forEach(match => {
              const valueMatch = match.match(/s:\d+:"([^"]+)"/);
              if (valueMatch) {
                values.push(valueMatch[1]);
              }
            });
          }
          
          return values;
        }
      } catch (error) {
        console.error('Error parsing PHP serialized array:', error);
      }
    }
    
    // If it's already an array, return it
    if (Array.isArray(serializedString)) {
      return serializedString;
    }
    
    return [];
  };

  // Function to calculate next upcoming occurrence
  const calculateNextOccurrence = (event: Event): string | null => {
    if (!event.recurrence) {
      return null;
    }

    const startDate = new Date(event.starts);
    
    if (!startDate || isNaN(startDate.getTime())) {
      return null;
    }
    
    const now = new Date();
    

    
    // If the start date is in the future, that's the next occurrence
    if (startDate > now) {
      return startDate.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }

    // Calculate next occurrence based on recurrence pattern
    let nextDate = new Date(startDate);
    
    switch (event.recurrence.toLowerCase()) {
      case 'daily':
        const dailyDays = event.r_daily_days || 1;
        // If start date is in the future, return it directly
        if (startDate > now) {
          nextDate = new Date(startDate);
        } else {
          // Calculate how many days have passed since start
          const daysDiff = Math.floor((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
          const cyclesPassed = Math.floor(daysDiff / dailyDays);
          const nextCycle = cyclesPassed + 1;
          nextDate = new Date(startDate.getTime() + (nextCycle * dailyDays * 24 * 60 * 60 * 1000));
        }
        break;
      
      case 'weekly':
        const weeklyDays = parsePHPSerializedArray(event.r_weekly_day);
        const weeklyWeeks = event.r_weekly_weeks || 1;
        
        // If start date is in the future, return it directly
        if (startDate > now) {
          nextDate = new Date(startDate);
        } else if (weeklyDays.length > 0) {
          // Start from the original start date and find the next occurrence after today
          let currentDate = new Date(startDate);
          let found = false;
          let attempts = 0;
          const maxAttempts = 52; // Prevent infinite loop
          
          while (!found && attempts < maxAttempts) {
            const dayName = currentDate.toLocaleDateString('en-US', { weekday: 'short' });
            if (weeklyDays.includes(dayName)) {
              // Check if this occurrence is after today
              if (currentDate > now) {
                nextDate = new Date(currentDate);
                found = true;
              }
            }
            if (!found) {
              // Move to the next week interval
              currentDate = new Date(currentDate.getTime() + (weeklyWeeks * 7 * 24 * 60 * 60 * 1000));
            }
            attempts++;
          }
        } else {
          // If no specific days selected, just add weeks from start date
          while (nextDate <= now) {
            nextDate.setDate(nextDate.getDate() + (weeklyWeeks * 7));
          }
        }
        break;
      
      case 'monthly':
        const monthlyMonths = event.r_monthly_months || 1;
        // If start date is in the future, return it directly
        if (startDate > now) {
          nextDate = new Date(startDate);
        } else {
          // Calculate how many months have passed since start
          const monthsDiff = (now.getFullYear() - startDate.getFullYear()) * 12 + (now.getMonth() - startDate.getMonth());
          const cyclesPassed = Math.floor(monthsDiff / monthlyMonths);
          const nextCycle = cyclesPassed + 1;
          nextDate = new Date(startDate.getFullYear(), startDate.getMonth() + (nextCycle * monthlyMonths), startDate.getDate());
        }
        break;
      
      case 'yearly':
        // If start date is in the future, return it directly
        if (startDate > now) {
          nextDate = new Date(startDate);
        } else {
          // Calculate how many years have passed since start
          const yearsDiff = now.getFullYear() - startDate.getFullYear();
          const nextYear = startDate.getFullYear() + yearsDiff + 1;
          nextDate = new Date(nextYear, startDate.getMonth(), startDate.getDate());
        }
        break;
      
      default:
        return null;
    }

    // Check if we've exceeded the end date
    if (event.r_end_date) {
      const endDate = new Date(event.r_end_date);
      if (nextDate > endDate) {
        return null; // No more occurrences
      }
    }

    return nextDate.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Function to format start/end times for display
  const formatTimeDisplay = (dateString: string, isRecurring: boolean = false) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    if (isRecurring) {
      // For recurring events, show just the time
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } else {
      // For non-recurring events, show the full date/time
      return date.toLocaleString('en-US', { 
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
  };

  // Function to generate recurrence summary based on event data
  const generateRecurrenceSummary = (event: Event) => {
    if (!event.recurrence) {
      return null;
    }

    let summary = "";

    switch (event.recurrence.toLowerCase()) {
      case 'daily':
        const dailyDays = event.r_daily_days || 1;
        summary = `This event repeats every ${dailyDays} day${dailyDays > 1 ? 's' : ''}.`;
        break;
      
      case 'weekly':
        const weeklyDays = parsePHPSerializedArray(event.r_weekly_day);
        const weeklyWeeks = event.r_weekly_weeks || 1;
        
        if (weeklyDays.length > 0) {
          const dayNames = weeklyDays.map(day => {
            const dayMap = {
              'Mon': 'Monday',
              'Tue': 'Tuesday', 
              'Wed': 'Wednesday',
              'Thu': 'Thursday',
              'Fri': 'Friday',
              'Sat': 'Saturday',
              'Sun': 'Sunday'
            };
            return dayMap[day] || day;
          });
          
          if (dayNames.length === 1) {
            summary = `This event repeats every ${weeklyWeeks} week${weeklyWeeks > 1 ? 's' : ''} on ${dayNames[0]}.`;
          } else {
            const lastDay = dayNames.pop();
            summary = `This event repeats every ${weeklyWeeks} week${weeklyWeeks > 1 ? 's' : ''} on ${dayNames.join(', ')} and ${lastDay}.`;
          }
        } else {
          summary = `This event repeats every ${weeklyWeeks} week${weeklyWeeks > 1 ? 's' : ''}.`;
        }
        break;
      
      case 'monthly':
        const monthlyMonths = event.r_monthly_months || 1;
        const monthlyDay = event.r_monthly_day || 1;
        const monthlyOccurrence = event.r_monthly_occurrence;
        const monthlyDaytext = event.r_monthly_weekday;
        
        if (monthlyOccurrence && monthlyDaytext) {
          summary = `This event repeats every ${monthlyMonths} month${monthlyMonths > 1 ? 's' : ''} on the ${monthlyOccurrence.toLowerCase()} ${monthlyDaytext.toLowerCase()}.`;
        } else {
          summary = `This event repeats every ${monthlyMonths} month${monthlyMonths > 1 ? 's' : ''} on day ${monthlyDay}.`;
        }
        break;
      
      case 'yearly':
        const yearlyMonth = event.r_yearly_month;
        const yearlyDay = event.r_yearly_day || 1;
        const yearlyOccurrence = event.r_yearly_occurrence;
        const yearlyDaytext = event.r_yearly_weekday;
        
        if (yearlyOccurrence && yearlyDaytext) {
          summary = `This event repeats every year on the ${yearlyOccurrence.toLowerCase()} ${yearlyDaytext.toLowerCase()} of ${yearlyMonth}.`;
        } else if (yearlyMonth) {
          summary = `This event repeats every year on ${yearlyMonth} ${yearlyDay}.`;
        } else {
          summary = "This event repeats every year.";
        }
        break;
      
      default:
        return null;
    }

    // Add next occurrence
    const nextOccurrence = calculateNextOccurrence(event);
    if (nextOccurrence) {
      summary += ` Next occurrence: ${nextOccurrence}.`;
    }

    // Add end condition
    if (event.r_end_type === 'After' && event.r_number_of_occurrences && event.r_number_of_occurrences > 0) {
      summary += ` The event will occur ${event.r_number_of_occurrences} time${event.r_number_of_occurrences > 1 ? 's' : ''}.`;
    } else if (event.r_end_date) {
      const endDate = new Date(event.r_end_date);
      const formattedEndDate = endDate.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      summary += ` The event will end on ${formattedEndDate}.`;
    }

    return summary;
  };

  const getEvent = () => {
    let data = {} as Event;
    axios.get(`${apiHost}/events/get/${match.params.slug}`)
      .then(res => {
        data = res.data as Event;
        if (res.status == 200) {
          let favorites = JSON.parse(localStorage.getItem("favorites")) || [];
          if (favorites.length > 0) {
            data.favorited = favorites.find(f => f.id == data.id && f.type == "event");
          }
          setEvent(data);
          setLoading(false);
        }
        return res;
      })
      .catch(() => {
        setLoading(false);
      });

    return data;
  };


  const getVariables = () => {
    let data = {} as Variables;
    axios.get(`${apiHost}/variables/get/global`)
      .then(res => {
        data = res.data as Variables;
        if (res.status == 200) {
          console.log(data);
          setVariables(data as Variables);
        }
        return res;
      })
      .catch(err => console.log(err));

    return data;
  };

  const addFavorite = (destination) => {
    let favorites = JSON.parse(localStorage.getItem("favorites")) || [];
    destination['type'] = "event";
    if (!destination.favorited) {
      favorites.push(destination);
      localStorage.setItem("favorites", JSON.stringify(favorites));
    }
    window.location.href = "/favorites";
  }

  useEffect(() => {
    window.scrollTo(0, 0);
    getEvent();
    getVariables();
  }, []);

  return (
    <PageSlideFade>
      <Helmet>
        <title>{event.title}</title>
        <meta name="description" content={event.meta_description} />
      </Helmet>

      {loading && (
        <Container maxW={'5xl'} pt={10} pb={10}>
          <Box>
            <Skeleton height="750px" />
          </Box>
        </Container>
      )}
      
      {!loading && !event.slug && (
        <Box>
          <Container maxW={'5xl'}>
            <Heading
              as={'h3'}
              fontFamily="american"
              fontWeight={600}
              fontSize={{ base: '2xl', sm: '3xl', md: '4xl' }}
              lineHeight={'100%'}
              textAlign="left"
              mt={{ base: 10, md: 5 }}>
              404 Page Not Found
            </Heading>
            <Box textAlign="left">
              <Button as={Link} href="/">Go to Home Page</Button>
            </Box>
          </Container>
        </Box>
      )}

      {!loading && event.slug && (
        <Box>
          {event.header_img ? (
            <Box className="home-header-container" backgroundImage={`url(${event.header_img})`} backgroundPosition="center center" backgroundSize="cover" minHeight="60dvh">
              <Container maxW={'5xl'}>

              </Container>
            </Box>
          ) : null}
          <Box bg="#143b49">
            <Container maxW={'5xl'}>
              <Grid templateColumns='repeat(6, 1fr)'>
                <GridItem colSpan={{ base: 12, md: 6 }}>
                  <Text
                    as="h1"
                    textAlign="left"
                    fontFamily="american"
                    fontWeight={600}
                    fontSize={{ base: '3xl', sm: '4xl', md: '5xl' }}
                    lineHeight={'120%'}
                    color="#fbeec8"
                    mt={{ base: 4, md: 0 }}
                  >{event.title}</Text>
                </GridItem>
              </Grid>
            </Container>
          </Box>
          <Box bg="rgb(251, 238, 200)" minHeight="810px">
            <Container maxW={'5xl'}>
              <Stack direction={{ base: 'column', md: 'row' }}>
                <Box w={{ base: '100%', md: '50%', lg: '65%' }} >
                  <Box>
                    {event.main_img ? (
                      <Image
                        src={`${event.main_img}`}
                        w={'100%'}
                        roundedBottomLeft={20}
                        alt={'main image'} />
                    ) : null}
                    {!event.main_img && variables.default_event_img ? (
                      <Image
                        src={`${variables.default_event_img}`}
                        w={'100%'}
                        roundedBottomLeft={20}
                        alt={'main image'} />
                    ) : null}
                  </Box>
                </Box>
                <Box w={{ base: '100%', md: '50%', lg: '35%' }} textAlign="left" bg="white" pl={10} pr={10} pb={10} roundedBottomRight={20}>
                  <Heading
                    as={'h3'}
                    fontFamily="american"
                    fontWeight={600}
                    fontSize={{ base: '2xl', sm: '3xl', md: '4xl' }}
                    lineHeight={'100%'}
                    textAlign="left"
                    mt={{ base: 10, md: 5 }}>
                    <ChakraIcon as={FaHeart} color={event.favorited ? "red" : "black"} mr={2} cursor="pointer" float="right" marginRight="-10px" onClick={() => { addFavorite(event); }} />
                    {event.title} 
                  </Heading>
                  {event.summary ? (
                    <Text mb={5} color="gray.500">{event.summary}</Text>
                  ) : null}
                  {event.display_dates !== false && event.display_dates !== 0 && (
                    <>
                      <Text>Starts: {formatTimeDisplay(event.starts, !!event.recurrence)}</Text>
                      {event.ends ? (
                        <Text>Ends: {formatTimeDisplay(event.ends, !!event.recurrence)}</Text>
                      ) : null}
                    </>
                  )}

                  {/* Recurrence Summary */}
                  {generateRecurrenceSummary(event) && (
                    <Box mt={4} p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                      <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                        {generateRecurrenceSummary(event)}
                      </Text>
                    </Box>
                  )}

                  <Stack mt={5}>
                    {event.google_link ? (
                      <Button as={Link} href={`${event.google_link}`} target="_blank" borderTopStartRadius={15} borderBottomStartRadius={15} borderRadius={15} fontSize="0.7em" bg="#D51F0C" color="white">Website</Button>
                    ) : null}
                    {event.website ? (
                      <Button as={Link} href={`${event.website}`} target="_blank" borderTopStartRadius={15} borderBottomStartRadius={15} borderRadius={15} fontSize="0.7em" bg="#D51F0C" color="white">Website</Button>
                    ) : null}
                    <ShareModal page={event} path="events/" />
                  </Stack>

                </Box>
              </Stack>

              <Box pb={10}>
                {event.content != null ? (
                  <Box bg="white" className="content-block" borderRadius={10} mt={2} p={4} textAlign="left">
                    {parser(event.content)}
                  </Box>
                ) : null}
              </Box>

              </Container>

              {/* Show map only if event has location data */}
              {event.location_formatted_latitude && event.location_formatted_longitude && (
                  <Box mt={10}>
                    <MapContainer 
                      center={[event.location_formatted_latitude, event.location_formatted_longitude]} 
                      zoom={12} 
                      scrollWheelZoom={false} 
                      style={{ minHeight: L.Browser.mobile ? '50vh' : '60vh' }} 
                      tap={!L.Browser.mobile} 
                      dragging={!L.Browser.mobile}
                    >
                      <ChangeView center={[event.location_formatted_latitude, event.location_formatted_longitude]} zoom={13} />
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <Marker 
                        position={[event.location_formatted_latitude, event.location_formatted_longitude]}
                        icon={new Icon({ iconUrl: markerIconPng, iconSize: [25, 41], iconAnchor: [12, 41] })}
                      >
                        <Popup>
                          <strong>{event.title}</strong>
                        </Popup>
                      </Marker>
                    </MapContainer>
                  </Box>
              )}

              <Box pb={10}>
                <Button as={Link} href={'/events'} borderTopStartRadius={15} borderBottomStartRadius={15} borderRadius={15} fontSize="0.7em" bg="#D51F0C" color="white" marginTop={10}>Back to All Events</Button>
              </Box>

          </Box>

        </Box>
      )}
    </PageSlideFade >
  );
};

export default Event;
